import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchTodaysBetsWithRace,
  OpenBetWithRace,
  calculateBetPl,
  calculateBetReturn,
  UserBet
} from '../services/betService';
import { fetchProfile } from '../services/profileService';
import { formatTime, formatRaceSpecs } from '../utils/helpers';
import { Wallet, TrendingUp, Hash, Coins, LineChart, Crown, Check, X, Clock, Ban, Minus } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { getFavouriteInfo } from '../utils/favouriteUtils';
import './OpenBets.css';

type StatusFilter = 'all' | UserBet['status'];

const SETTLED: UserBet['status'][] = ['won', 'lost', 'void'];

const OpenBets: React.FC = () => {
  const { user } = useAuth();
  const [bets, setBets] = useState<OpenBetWithRace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'race_time' | 'stake' | 'placed_at' | 'odds'>('race_time');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [hideUnmatched, setHideUnmatched] = useState(true);
  const [currency, setCurrency] = useState('GBP');

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) {
        setLoading(false);
        setBets([]);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const profile = await fetchProfile(user.id);
        if (profile?.currency) {
          setCurrency(profile.currency);
        }

        const todays = await fetchTodaysBetsWithRace(user.id);
        setBets(todays);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to load today's bets";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.id]);

  const getCurrencySymbol = (curr: string): string => {
    const symbols: { [key: string]: string } = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      AUD: 'A$',
      CAD: 'C$'
    };
    return symbols[curr] || curr;
  };

  const sym = getCurrencySymbol(currency);

  const filteredBets = useMemo(() => {
    let list = statusFilter === 'all' ? bets : bets.filter(b => b.status === statusFilter);
    if (hideUnmatched) {
      list = list.filter(b => b.status !== 'unmatched');
    }
    return list;
  }, [bets, statusFilter, hideUnmatched]);

  const pendingToday = useMemo(() => bets.filter(b => b.status === 'pending'), [bets]);

  const settledToday = useMemo(() => bets.filter(b => SETTLED.includes(b.status)), [bets]);

  const statusCounts = useMemo((): Record<StatusFilter, number> => {
    let pending = 0;
    let won = 0;
    let lost = 0;
    let voided = 0;
    let unmatched = 0;
    for (const b of bets) {
      switch (b.status) {
        case 'pending':
          pending++;
          break;
        case 'won':
          won++;
          break;
        case 'lost':
          lost++;
          break;
        case 'void':
          voided++;
          break;
        case 'unmatched':
          unmatched++;
          break;
        default:
          break;
      }
    }
    const allCount = hideUnmatched
      ? bets.filter(b => b.status !== 'unmatched').length
      : bets.length;

    return {
      all: allCount,
      pending,
      won,
      lost,
      void: voided,
      unmatched
    };
  }, [bets, hideUnmatched]);

  const summaryStats = useMemo(() => {
    const count = filteredBets.length;
    const totalStaked = filteredBets.reduce((s, b) => s + b.stake, 0);

    const pendingForOpenTile =
      statusFilter === 'all' ? pendingToday : statusFilter === 'pending' ? filteredBets : [];

    const openPotentialReturn = pendingForOpenTile.reduce((s, b) => s + b.stake * b.odds, 0);
    const openPotentialProfit = pendingForOpenTile.reduce((s, b) => s + b.potential_profit, 0);

    const settledForPlTile =
      statusFilter === 'all'
        ? settledToday
        : SETTLED.includes(statusFilter as UserBet['status'])
          ? filteredBets
          : [];

    const netPlSettled = settledForPlTile.reduce((s, b) => s + calculateBetPl(b), 0);
    const totalReturnedSettled = settledForPlTile.reduce((s, b) => {
      const r = calculateBetReturn(b);
      return s + (r === null ? 0 : r);
    }, 0);

    return {
      count,
      totalStaked,
      showPotentialTiles: statusFilter === 'all' || statusFilter === 'pending',
      openPotentialReturn,
      openPotentialProfit,
      showSettledTile: statusFilter === 'all' || SETTLED.includes(statusFilter as UserBet['status']),
      netPlSettled,
      totalReturnedSettled
    };
  }, [filteredBets, pendingToday, settledToday, statusFilter]);

  const sortedBets = useMemo(() => {
    const sorted = [...filteredBets];
    switch (sortBy) {
      case 'race_time':
        return sorted.sort((a, b) => {
          if (!a.race || !b.race) return 0;
          const timeA = `${a.race.race_date} ${a.race.race_time}`;
          const timeB = `${b.race.race_date} ${b.race.race_time}`;
          return timeA.localeCompare(timeB);
        });
      case 'stake':
        return sorted.sort((a, b) => b.stake - a.stake);
      case 'placed_at':
        return sorted.sort((a, b) => new Date(b.placed_at).getTime() - new Date(a.placed_at).getTime());
      case 'odds':
        return sorted.sort((a, b) => b.odds - a.odds);
      default:
        return sorted;
    }
  }, [filteredBets, sortBy]);

  const statusLabel = useCallback((s: UserBet['status']) => {
    switch (s) {
      case 'pending':
        return 'Pending';
      case 'won':
        return 'Won';
      case 'lost':
        return 'Lost';
      case 'void':
        return 'Void';
      case 'unmatched':
        return 'Unmatched';
      default:
        return s;
    }
  }, []);

  const renderStatusBadge = useCallback(
    (status: UserBet['status']) => {
      const label = statusLabel(status);
      const iconProps = { size: 14, 'aria-hidden': true as const };

      let icon: React.ReactNode;
      switch (status) {
        case 'won':
          icon = <Check {...iconProps} />;
          break;
        case 'lost':
          icon = <X {...iconProps} />;
          break;
        case 'pending':
          icon = <Clock {...iconProps} />;
          break;
        case 'void':
          icon = <Ban {...iconProps} />;
          break;
        case 'unmatched':
          icon = <Minus {...iconProps} />;
          break;
        default:
          icon = null;
      }

      return (
        <span className={`bet-status-pill status-${status}`}>
          {icon}
          {label}
        </span>
      );
    },
    [statusLabel]
  );

  const formatPl = (n: number) => {
    const sign = n > 0 ? '+' : '';
    return `${sign}${sym}${n.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="open-bets">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="open-bets">
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div className="open-bets">
      <div className="open-bets-header">
        <h1>{"Today's Bets"}</h1>
      </div>

      {statusFilter !== 'unmatched' && (
        <div className="open-bets-summary-stats">
          <div className="summary-tile">
            <div className="summary-icon">
              <Hash size={20} />
            </div>
            <div className="summary-value">{summaryStats.count}</div>
            <div className="summary-label">Bets</div>
          </div>

          <div className="summary-tile">
            <div className="summary-icon">
              <Wallet size={20} />
            </div>
            <div className="summary-value">
              {sym}
              {summaryStats.totalStaked.toFixed(2)}
            </div>
            <div className="summary-label">Total staked</div>
          </div>

          {summaryStats.showPotentialTiles && (
            <div className="summary-tile">
              <div className="summary-icon">
                <Coins size={20} />
              </div>
              <div className="summary-value">
                {sym}
                {summaryStats.openPotentialReturn.toFixed(2)}
              </div>
              <div className="summary-label">Potential return</div>
            </div>
          )}

          {summaryStats.showPotentialTiles && (
            <div className="summary-tile">
              <div className="summary-icon">
                <LineChart size={20} />
              </div>
              <div className="summary-value">
                {sym}
                {summaryStats.openPotentialProfit.toFixed(2)}
              </div>
              <div className="summary-label">Potential profit</div>
            </div>
          )}

          {summaryStats.showSettledTile && (
            <div className={`summary-tile ${summaryStats.netPlSettled >= 0 ? 'profit' : 'loss'}`}>
              <div className="summary-icon">
                <TrendingUp size={20} />
              </div>
              <div className="summary-value">{formatPl(summaryStats.netPlSettled)}</div>
              <div className="summary-label">Net P/L</div>
            </div>
          )}

          {summaryStats.showSettledTile && (
            <div className="summary-tile">
              <div className="summary-icon">
                <Coins size={20} />
              </div>
              <div className="summary-value">
                {sym}
                {summaryStats.totalReturnedSettled.toFixed(2)}
              </div>
              <div className="summary-label">Total returned</div>
            </div>
          )}
        </div>
      )}

      <div className="open-bets-controls">
        <div className="status-filters" role="tablist" aria-label="Bet status">
          {(
            [
              ['all', 'All'],
              ['pending', 'Pending'],
              ['won', 'Won'],
              ['lost', 'Lost'],
              ['void', 'Void'],
              ['unmatched', 'Unmatched']
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={statusFilter === value}
              className={`status-filter-btn ${statusFilter === value ? 'active' : ''}`}
              onClick={() => setStatusFilter(value)}
            >
              {label} ({statusCounts[value]})
            </button>
          ))}
        </div>

        <div className="filters-sort-group">
          <label className="hide-unmatched-label">
            <input
              id="hide-unmatched-checkbox"
              type="checkbox"
              checked={hideUnmatched}
              onChange={e => setHideUnmatched(e.target.checked)}
              className="hide-unmatched-checkbox"
            />
            <span>Hide Unmatched</span>
          </label>
          <div className="sort-control">
            <label htmlFor="sort-select">Sort by:</label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value as typeof sortBy)}
              className="sort-select"
            >
              <option value="race_time">Race Time</option>
              <option value="stake">Stake</option>
              <option value="placed_at">Time Placed</option>
              <option value="odds">Odds</option>
            </select>
          </div>
        </div>
      </div>

      <div className="open-bets-list">
        {bets.length === 0 ? (
          <div className="no-bets-message">
            <p>You have no bets placed today (UTC).</p>
          </div>
        ) : sortedBets.length === 0 ? (
          <div className="no-bets-message">
            <p>No bets match this status for today.</p>
          </div>
        ) : (
          sortedBets.map(bet => (
            <div key={bet.id} className="open-bet-card">
              <div className="open-bet-card-header">
                <div className="open-bet-race-info">
                  {bet.race && (
                    <>
                      <div className="open-bet-race-time-course">
                        {formatTime(bet.race.race_time)} {bet.race.course}
                      </div>
                      <div className="open-bet-race-specs">
                        {formatRaceSpecs(bet.race)}
                      </div>
                    </>
                  )}
                </div>
                <div className="open-bet-selection-header">
                  {renderStatusBadge(bet.status)}
                  {(() => {
                    const selectionName = bet.runner?.horse_name || bet.race?.ai_selection || 'Unknown';
                    const { isFavourite, label: favouriteLabel } = getFavouriteInfo(
                      selectionName,
                      bet.raceRunners ?? []
                    );
                    return (
                      <div
                        className="open-bet-selection-name"
                        data-tooltip={favouriteLabel || undefined}
                      >
                        {isFavourite && (
                          <span
                            className="favourite-indicator"
                            aria-label={favouriteLabel || undefined}
                          >
                            <Crown size={14} />
                          </span>
                        )}
                        <span className="selection-text">{selectionName}</span>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="open-bet-card-body">
                <div className="open-bet-details">
                  <div className="open-bet-detail-item">
                    <span className="open-bet-detail-label">Stake:</span>
                    <span className="open-bet-detail-value">
                      {sym}
                      {bet.stake.toFixed(2)}
                    </span>
                  </div>
                  <div className="open-bet-detail-item">
                    <span className="open-bet-detail-label">Odds:</span>
                    <span className="open-bet-detail-value">{bet.odds.toFixed(2)}</span>
                  </div>

                  {bet.status === 'pending' && (
                    <>
                      <div className="open-bet-detail-item">
                        <span className="open-bet-detail-label">Potential return:</span>
                        <span className="open-bet-detail-value">
                          {sym}
                          {(bet.stake * bet.odds).toFixed(2)}
                        </span>
                      </div>
                      <div className="open-bet-detail-item profit">
                        <span className="open-bet-detail-label">Potential profit:</span>
                        <span className="open-bet-detail-value">
                          {sym}
                          {bet.potential_profit.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}

                  {SETTLED.includes(bet.status) && (
                    <>
                      <div className="open-bet-detail-item">
                        <span className="open-bet-detail-label">Return:</span>
                        <span className="open-bet-detail-value">
                          {(() => {
                            const r = calculateBetReturn(bet);
                            return r === null ? '—' : `${sym}${r.toFixed(2)}`;
                          })()}
                        </span>
                      </div>
                      <div className={`open-bet-detail-item ${calculateBetPl(bet) >= 0 ? 'profit' : 'loss'}`}>
                        <span className="open-bet-detail-label">P/L:</span>
                        <span className="open-bet-detail-value">{formatPl(calculateBetPl(bet))}</span>
                      </div>
                      <div className="open-bet-detail-item">
                        <span className="open-bet-detail-label">Settled:</span>
                        <span className="open-bet-detail-value">
                          {bet.settled_at ? new Date(bet.settled_at).toLocaleString() : '—'}
                        </span>
                      </div>
                    </>
                  )}

                  {bet.status === 'unmatched' && (
                    <div className="open-bet-detail-item full-width">
                      <span className="open-bet-detail-label">Status:</span>
                      <span className="open-bet-detail-value unmatched-copy">
                        Not matched on the exchange yet — potential return/profit apply only after matching.
                      </span>
                    </div>
                  )}

                  <div className="open-bet-detail-item">
                    <span className="open-bet-detail-label">Bookmaker:</span>
                    <span className="open-bet-detail-value">{bet.bookmaker}</span>
                  </div>
                  <div className="open-bet-detail-item">
                    <span className="open-bet-detail-label">Placed (UTC):</span>
                    <span className="open-bet-detail-value">
                      {new Date(bet.placed_at).toLocaleString('en-GB', { timeZone: 'UTC' })} UTC
                    </span>
                  </div>
                </div>

                {bet.notes && (
                  <div className="open-bet-notes">
                    <div className="open-bet-notes-label">Notes:</div>
                    <div className="open-bet-notes-content">{bet.notes}</div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OpenBets;
